import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  List, 
  Checkbox, 
  Space, 
  Typography,
  Select,
  Alert,
  Spin,
  message,
  Card,
  Avatar,
  Tag,
  Divider,
  Tooltip
} from 'antd';
import { 
  PhoneOutlined,
  UserOutlined,
  MessageOutlined,
  SendOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, addDoc } from 'firebase/firestore';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

/**
 * MassContactModal - Send SMS messages to multiple site managers via Twilio
 */
const MassContactModal = ({ visible, onClose, selectedSites = [] }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [sendResults, setSendResults] = useState(null);
  const [step, setStep] = useState(1); // 1: compose, 2: confirm, 3: results

  // Message templates
  const MESSAGE_TEMPLATES = {
    emergency: 'URGENT: Crisis Alert - Please confirm staff safety and operational status at {site_name}. Respond immediately. - Crisis Management Team',
    safety_check: 'Safety Check: Please confirm all staff are safe at {site_name} and report any issues. Reply with status update. - Crisis Team',
    operational_update: 'Operations Update: Please provide current operational status for {site_name}. Include any disruptions or concerns. - Crisis Team',
    evacuation: 'EVACUATION NOTICE: Immediate evacuation required at {site_name}. Follow emergency procedures. Confirm when complete. - Crisis Team',
    all_clear: 'All Clear: Crisis situation has been resolved. Normal operations may resume at {site_name}. Thank you for your cooperation. - Crisis Team',
    custom: ''
  };

  // Initialize selected recipients on mount
  useEffect(() => {
    if (visible && selectedSites.length > 0) {
      const recipients = selectedSites
        .filter(site => site.site_manager_mobile && site.site_manager_name)
        .map(site => ({
          id: site.id,
          site_name: site.site_name,
          site_type: site.site_type,
          manager_name: site.site_manager_name,
          mobile: site.site_manager_mobile,
          email: site.site_manager_email || null,
          selected: true
        }));
      
      setSelectedRecipients(recipients);
      setStep(1);
      setSendResults(null);
      form.resetFields();
    }
  }, [visible, selectedSites, form]);

  // Handle template selection
  const handleTemplateChange = (templateKey) => {
    const template = MESSAGE_TEMPLATES[templateKey];
    setMessageTemplate(template);
    form.setFieldsValue({ message: template });
  };

  // Handle recipient selection
  const handleRecipientToggle = (recipientId, checked) => {
    setSelectedRecipients(prev => 
      prev.map(recipient => 
        recipient.id === recipientId 
          ? { ...recipient, selected: checked }
          : recipient
      )
    );
  };

  // Select/deselect all recipients
  const handleSelectAll = (checked) => {
    setSelectedRecipients(prev => 
      prev.map(recipient => ({ ...recipient, selected: checked }))
    );
  };

  // Process message for each recipient (replace placeholders)
  const processMessage = (message, site) => {
    return message
      .replace(/{site_name}/g, site.site_name)
      .replace(/{manager_name}/g, site.manager_name)
      .replace(/{site_type}/g, site.site_type);
  };

  // Send SMS messages
  const sendMessages = async () => {
    try {
      setLoading(true);
      const selectedContacts = selectedRecipients.filter(r => r.selected);
      const formValues = await form.validateFields();
      
      if (selectedContacts.length === 0) {
        message.warning('Please select at least one recipient');
        return;
      }

      const results = {
        total: selectedContacts.length,
        successful: 0,
        failed: 0,
        details: []
      };

      // Send messages via server endpoint
      for (const contact of selectedContacts) {
        try {
          const processedMessage = processMessage(formValues.message, contact);
          
          // Extract incidentId for SMS tracking
          const urlParams = new URLSearchParams(window.location.search);
          const incidentId = urlParams.get('id') || window.location.pathname.split('/').pop();
          
          const response = await fetch('/api/send-sms', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              to: contact.mobile,
              message: processedMessage,
              orgId: currentUser.orgId,
              userId: currentUser.uid,
              incidentId: (incidentId && incidentId !== 'map') ? incidentId : null
            })
          });

          if (response.ok) {
            results.successful++;
            results.details.push({
              recipient: contact,
              status: 'success',
              message: 'Message sent successfully'
            });

            // Log to communications
            await logCommunication(contact, processedMessage, 'sent');
            
          } else {
            const errorData = await response.json();
            results.failed++;
            results.details.push({
              recipient: contact,
              status: 'failed',
              message: errorData.error || 'Failed to send message'
            });
          }
        } catch (error) {
          console.error('Error sending SMS to', contact.mobile, ':', error);
          results.failed++;
          results.details.push({
            recipient: contact,
            status: 'failed',
            message: error.message || 'Network error'
          });
        }
      }

      setSendResults(results);
      setStep(3);
      
      if (results.successful > 0) {
        message.success(`Successfully sent ${results.successful} messages`);
      }
      if (results.failed > 0) {
        message.error(`Failed to send ${results.failed} messages`);
      }

    } catch (error) {
      console.error('Error in mass SMS send:', error);
      message.error('Failed to send messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Log communication to Firestore
  const logCommunication = async (contact, message, status) => {
    try {
      const commLog = {
        type: 'sms_mass_contact',
        recipient_name: contact.manager_name,
        recipient_mobile: contact.mobile,
        site_id: contact.id,
        site_name: contact.site_name,
        message: message,
        status: status,
        sent_by: currentUser.uid,
        sent_by_name: currentUser.displayName || currentUser.email,
        org_id: currentUser.orgId,
        timestamp: new Date(),
        created_at: new Date()
      };

      // Add to communications log
      const commsRef = collection(db, 'c3_messages');
      await addDoc(commsRef, commLog);
      
    } catch (error) {
      console.error('Error logging communication:', error);
    }
  };

  // Reset modal state
  const handleClose = () => {
    setStep(1);
    setSendResults(null);
    form.resetFields();
    setSelectedRecipients([]);
    setMessageTemplate('');
    onClose();
  };

  // Get selected count
  const selectedCount = selectedRecipients.filter(r => r.selected).length;

  // Render step 1: Compose message
  const renderComposeStep = () => (
    <div>
      <Alert
        message="Mass SMS Communication"
        description="Send SMS messages to selected site managers. Messages will be logged in the communications system."
        type="info"
        showIcon
        className="mb-3"
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          template: 'custom',
          message: ''
        }}
      >
        <Form.Item
          label="Message Template"
          name="template"
        >
          <Select
            placeholder="Select a template or choose custom"
            onChange={handleTemplateChange}
          >
            <Option value="emergency">🚨 Emergency Alert</Option>
            <Option value="safety_check">✅ Safety Check</Option>
            <Option value="operational_update">📊 Operational Update</Option>
            <Option value="evacuation">⚠️ Evacuation Notice</Option>
            <Option value="all_clear">🆗 All Clear</Option>
            <Option value="custom">📝 Custom Message</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Message Content"
          name="message"
          rules={[
            { required: true, message: 'Please enter a message' },
            { max: 1600, message: 'Message too long (max 1600 characters)' }
          ]}
          extra="Use {site_name} to include the site name in each message"
        >
          <TextArea
            rows={4}
            placeholder="Enter your message here..."
            showCount
            maxLength={1600}
          />
        </Form.Item>
      </Form>

      <Divider>Recipients ({selectedCount} selected)</Divider>

      <div className="mb-3">
        <Checkbox
          checked={selectedCount === selectedRecipients.length}
          indeterminate={selectedCount > 0 && selectedCount < selectedRecipients.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Select All Recipients
        </Checkbox>
      </div>

      <List
        dataSource={selectedRecipients}
        renderItem={recipient => (
          <List.Item className="recipient-item">
            <List.Item.Meta
              avatar={
                <Checkbox
                  checked={recipient.selected}
                  onChange={(e) => handleRecipientToggle(recipient.id, e.target.checked)}
                />
              }
              title={
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <Text strong>{recipient.manager_name}</Text>
                  <Tag size="small">{recipient.site_name}</Tag>
                </Space>
              }
              description={
                <Space>
                  <PhoneOutlined />
                  <Text type="secondary">{recipient.mobile}</Text>
                </Space>
              }
            />
          </List.Item>
        )}
        style={{ maxHeight: '200px', overflowY: 'auto' }}
      />
    </div>
  );

  // Render step 2: Confirmation
  const renderConfirmStep = () => {
    const selectedContacts = selectedRecipients.filter(r => r.selected);
    const formValues = form.getFieldsValue();
    
    return (
      <div>
        <Alert
          message="Confirm Mass SMS Send"
          description={`You are about to send ${selectedContacts.length} SMS messages. Please review the details below.`}
          type="warning"
          showIcon
          className="mb-3"
        />

        <Card title="Message Preview" className="mb-3">
          <Text code style={{ whiteSpace: 'pre-wrap' }}>
            {formValues.message}
          </Text>
        </Card>

        <Card title={`Recipients (${selectedContacts.length})`}>
          <List
            size="small"
            dataSource={selectedContacts}
            renderItem={contact => (
              <List.Item>
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <div>
                    <Text strong>{contact.manager_name}</Text>
                    <br />
                    <Text type="secondary">{contact.site_name} - {contact.mobile}</Text>
                  </div>
                </Space>
              </List.Item>
            )}
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          />
        </Card>
      </div>
    );
  };

  // Render step 3: Results
  const renderResultsStep = () => {
    if (!sendResults) return null;

    return (
      <div>
        <Alert
          message="Mass SMS Send Complete"
          description={`Sent ${sendResults.successful} messages successfully. ${sendResults.failed} failed.`}
          type={sendResults.failed === 0 ? 'success' : 'warning'}
          showIcon
          className="mb-3"
        />

        <Card title="Send Results">
          <List
            dataSource={sendResults.details}
            renderItem={result => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    result.status === 'success' 
                      ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      : <WarningOutlined style={{ color: '#fa8c16' }} />
                  }
                  title={
                    <Space>
                      <Text strong>{result.recipient.manager_name}</Text>
                      <Text type="secondary">({result.recipient.site_name})</Text>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">{result.recipient.mobile}</Text>
                      <br />
                      <Text 
                        type={result.status === 'success' ? 'success' : 'danger'}
                        style={{ fontSize: '12px' }}
                      >
                        {result.message}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          />
        </Card>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <span>Mass Contact Site Managers</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={
        <Space>
          {step > 1 && step < 3 && (
            <Tooltip title="Go back to the previous step">
              <Button onClick={() => setStep(step - 1)}>
                Back
              </Button>
            </Tooltip>
          )}
          
          {step === 1 && (
            <>
              <Tooltip title="Cancel and close this dialog">
                <Button onClick={handleClose}>
                  Cancel
                </Button>
              </Tooltip>
              <Tooltip title="Continue to message review with selected recipients">
                <Button 
                  type="primary"
                  onClick={() => setStep(2)}
                  disabled={selectedCount === 0}
                  style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                >
                  Next: Review ({selectedCount} recipients)
                </Button>
              </Tooltip>
            </>
          )}
          
          {step === 2 && (
            <>
              <Tooltip title="Cancel and close this dialog">
                <Button onClick={handleClose}>
                  Cancel
                </Button>
              </Tooltip>
              <Tooltip title="Send SMS messages to all selected recipients">
                <Button 
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendMessages}
                  loading={loading}
                  style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c' }}
                >
                  Send SMS Messages
                </Button>
              </Tooltip>
            </>
          )}
          
          {step === 3 && (
            <Tooltip title="Close this dialog and return to map">
              <Button type="primary" onClick={handleClose}>
                Close
              </Button>
            </Tooltip>
          )}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Sending messages...</Text>
          </div>
        </div>
      ) : (
        <>
          {step === 1 && renderComposeStep()}
          {step === 2 && renderConfirmStep()}
          {step === 3 && renderResultsStep()}
        </>
      )}
    </Modal>
  );
};

export default MassContactModal; 